import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Dropdown, Settings, WithTooltip } from 'basic-components';
import { HooksService, Query } from 'dashboard-services';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import { FocusTrap } from 'focus-trap-react';
import { When } from 'react-if';

const ESC_KEY = 27,
      emptyArray = [],
      emptyFun = () => {}
const ColumnSettings = () => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.source),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols) || emptyArray,
        selectedColumns = useSelector(state => state.seriesState.creatorState.selectedColumns) || emptyArray,
        [isOpen, setIsOpen] = useState(false),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        swapIsOpen = useCallback(() => setIsOpen(isOpen => {
          if(isOpen) {
            const close = e => {
              if(e.keyCode === ESC_KEY){
                swapIsOpen()
              }
            }
            addEventListener('keydown', close)
          } else {
            removeEventListener('keydown', close)
          }
          return !isOpen;
        }), []),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withHeader("Target", source, true)
        , [getApiConfigLocal, source]),
        { data: columns = emptyArray } = dataStorageQueries.useGetColumns({ getQueryBuilder, selectedSymbols, provider: source }),
        onChange = useCallback((value, { name }) => dispatch(creatorSeriesActions.onChange(value, name)), [dispatch]),
        prevColumns = HooksService.usePrevious(columns),
        selectedColumnsLength = selectedColumns?.length,
        prevSelectedColumnsLength = HooksService.usePrevious(selectedColumnsLength),
        columnsRef = useRef(),
        setDefaultColumns = useCallback(() => {
          columnsRef.current.dropdown.current.setValue([columns[0]])
          swapIsOpen()
        }, [columns, swapIsOpen]),
        applyToAll = useCallback(() => {
          swapIsOpen()
          selectedSymbols.forEach((symbol, index) => {
            if(symbol?.columns) {
              dispatch(creatorSeriesActions.onRemoveSymbolColumns(index))
            }
          })
        }, [dispatch, selectedSymbols, swapIsOpen]),
        timeoutRef = useRef(),
        onMenuOpen = useCallback(() => setIsMenuOpen(true), []),
        onMenuClose = useCallback(() => {
          timeoutRef.current = setTimeout(() => setIsMenuOpen(false), 100)
        }, []),
        handleOnKey = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            swapIsOpen()
          }
        }, [swapIsOpen])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
    removeEventListener('keydown', close)
  }, [])

  useEffect(() => {
    if(isOpen) {
      if(selectedColumns.includes("*") && columns?.length > 0) {
        columnsRef.current.dropdown.current.setValue(columns)
      } else if((!prevColumns || prevColumns?.length === 0) && columns[0] && selectedColumns.length === 0 && prevSelectedColumnsLength !== 1) {
        columnsRef.current.dropdown.current.setValue([columns[0]])
      }
    }
  }, [columns, isOpen, onChange, prevColumns, prevSelectedColumnsLength, selectedColumns])

  return (
    <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header data-storage__creator__add-data-panel__content__headers__header--columns">
      <When condition={isOpen}>
        {() => (
          <div 
              className="ng-office-app__authed__content__basket__body__data__basket__headers__header__bg"
              onClick={isMenuOpen ? emptyFun : swapIsOpen}
          />
        )}
      </When>
      <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__text">
        Columns
      </div>
      <WithTooltip
          data-place="left"
          short
          text="Set default column"
      >
        <div
            className="ng-office-app__authed__content__basket__body__data__basket__headers__header__icon"
            onClick={swapIsOpen}
            onKeyDown={handleOnKey}
            tabIndex={0}
        >
          <Settings
              color="blue-bright"
              height={12}
              width={12}
          />
        </div>
      </WithTooltip>
      <When condition={isOpen}>
        {() => (
          <FocusTrap active={isOpen}>
            <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings">
              <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__header">
                Default Columns
              </div>
              <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__dropdown">
                <Dropdown
                    autoFocus
                    defaultValue={selectedColumns}
                    disabled={selectedSymbols.length === 0}
                    menuPlacement="bottom"
                    multi
                    name="selectedColumns"
                    noError
                    onChange={onChange}
                    onMenuClose={onMenuClose}
                    onMenuOpen={onMenuOpen}
                    options={columns}
                    placeholder="Columns"
                    ref={columnsRef}
                    variant="border"
                />
              </div>
              <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__footer">
                <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__footer__left">
                  <Button
                      onClick={setDefaultColumns}
                      size="small"
                      variant="text"
                  >
                    Default
                  </Button>
                </div>
                <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__footer__right">
                  <Button
                      onClick={swapIsOpen}
                      size="small"
                      variant="text"
                  >
                    Cancel
                  </Button>
                  <Button
                      onClick={applyToAll}
                      size="small"
                      variant="secondary"
                  >
                    Apply to all
                  </Button>
                </div>
              </div>
            </div>
          </FocusTrap>
        )}
      </When>
    </div>
  )
}

export default memo(ColumnSettings);